import React from 'react'
import { ResultObjectType } from '@cbgms/api'
import CallToActionSearchPartsResult from './CallToActionSearchPartsResult'
import { TopSearchResultType, VirtualSearchResultType } from './TopSearchResultType'
import TyreSizeResult from './TyreSizeResult'
import { VehicleResult } from './VehicleResult'
import { WorkOrderResult } from './WorkOrderResult'
import { ErrorProvider } from '../../ErrorProvider'
import { AccountResult } from './AccountResult'
import CallToActionCreateVehicleManuallyResult from './CallToActionCreateVehicleManuallyResult'
import { InvoiceResult } from './InvoiceResult'
import { NoSearchResults } from './NoSearchResults'
import { MoreOrderResults, OrderResult } from './OrderResult'
import { QuotationResult } from './QuotationResult'
import { RecentlyOpenedResult } from '../../search-v1/recentlyOpened/components/RecentlyOpenedResult'
import { IVehicleSearchFormatResults } from '../../vehicle'

export interface ISearchResultWithTitle extends Omit<IVehicleSearchFormatResults, 'Type'> {
  Type: TopSearchResultType
  Title?: string // can be used to add title above search list
}

export interface ISearchResultDialogProps extends ISearchResultWithTitle {
  Type: ResultObjectType
  Score: number
  query: string
  focus?: boolean
  clearQuery: () => void
  primaryAction: () => Promise<void>
  hasGroupHeader: boolean
}

export interface ITopSearchResultProps extends ISearchResultWithTitle {
  query: string
  focus?: boolean
  clearQuery: () => void
  primaryAction: () => Promise<void> // primaryAction is the effect that should happen when selecting this search result
  hasGroupHeader: boolean // Grouped items can use a single header, instead of rendering a header for each item
}

const typeComponent = (searchResult: ITopSearchResultProps) => {
  const { Type: type, clearQuery, primaryAction, hasGroupHeader, query } = searchResult
  switch (type) {
    case ResultObjectType.Invoice:
      return (
        searchResult.WorkorderInvoice && (
          <InvoiceResult
            Invoice={searchResult.WorkorderInvoice}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case ResultObjectType.VehicleSalesQuotation:
      return (
        searchResult.VehicleSalesQuotation && (
          <QuotationResult
            Quotation={searchResult.VehicleSalesQuotation}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case ResultObjectType.WorkorderQuotation:
      return (
        searchResult.WorkorderQuotation && (
          <QuotationResult
            Quotation={searchResult.WorkorderQuotation}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case ResultObjectType.GenericVehicle:
    case ResultObjectType.DatabaseVehicle:
      return (
        searchResult.Vehicle && (
          <VehicleResult
            Vehicle={searchResult.Vehicle}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            clearQuery={clearQuery}
            primaryAction={primaryAction}
            selected={searchResult.focus}
          />
        )
      )
    case ResultObjectType.Account:
      return (
        searchResult.Account && (
          <AccountResult
            Account={searchResult.Account}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            clearQuery={clearQuery}
            primaryAction={primaryAction}
          />
        )
      )
    case ResultObjectType.Order:
      return (
        searchResult.Order && (
          <OrderResult
            Order={searchResult.Order}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            query={query}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case VirtualSearchResultType.CallToActionViewMoreSupplierOrder:
      return <MoreOrderResults primaryAction={primaryAction} />
    case ResultObjectType.WorkOrder:
    case ResultObjectType.WorkOrderArchive:
      return (
        searchResult.Workorder && (
          <WorkOrderResult
            WorkOrder={searchResult.Workorder}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case ResultObjectType.RecentlyOpened:
      return (
        searchResult.RecentlyOpened && (
          <RecentlyOpenedResult entity={searchResult.RecentlyOpened} primaryAction={primaryAction} hasGroupHeader={hasGroupHeader} />
        )
      )
    case ResultObjectType.TyreSize:
      return (
        searchResult.TyreSize && (
          <TyreSizeResult
            option={searchResult.TyreSize}
            Type={type}
            hasGroupHeader={hasGroupHeader}
            primaryAction={primaryAction}
            clearQuery={clearQuery}
          />
        )
      )
    case ResultObjectType.Error:
      return <ErrorProvider errors={searchResult.Errors!} />
    case VirtualSearchResultType.NoResults:
      return <NoSearchResults />
    case VirtualSearchResultType.CallToActionSearchParts:
      return (
        <CallToActionSearchPartsResult
          Type={type}
          query={query}
          hasGroupHeader={hasGroupHeader}
          clearQuery={clearQuery}
          primaryAction={primaryAction}
        />
      )
    case VirtualSearchResultType.CallToActionCreateVehicleManually:
      return (
        <CallToActionCreateVehicleManuallyResult
          Type={type}
          query={query}
          hasGroupHeader={hasGroupHeader}
          primaryAction={primaryAction}
          clearQuery={clearQuery}
        />
      )
    default:
      return null
  }
}

export const ResultComponentSelector: React.FC<ITopSearchResultProps> = props => {
  return <>{typeComponent(props)}</>
}
